"use client";

import { useState, useCallback } from "react";
import { FileText, Download, FileSpreadsheet, Loader2, ChevronDown } from "lucide-react";
import type { WorkSheet } from "xlsx";

type XlsxModule = typeof import("xlsx");

const MESES_ES = [
  "enero",
  "febrero",
  "marzo",
  "abril",
  "mayo",
  "junio",
  "julio",
  "agosto",
  "septiembre",
  "octubre",
  "noviembre",
  "diciembre",
];

interface FechaExcel {
  y: number;
  m: number;
  d: number;
  H: number;
  M: number;
  S: number;
}

function dosDigitos(n: number): string {
  return String(n).padStart(2, "0");
}

// Deja solo los tokens de fecha/hora del formato de Excel: quita los
// modificadores de locale ([$-540A]), los literales ("de") y los escapes.
function tokensDeFormato(formato: string): string {
  return formato
    .replace(/\[[^\]]*\]/g, "")
    .replace(/"[^"]*"/g, "")
    .replace(/\\./g, "")
    .toLowerCase();
}

function textoFecha(f: FechaExcel, formato: string): string {
  const tokens = tokensDeFormato(formato);
  const es12h = /am\/pm|a\/p/.test(tokens);
  const horas = es12h ? f.H % 12 || 12 : f.H;
  const hora =
    `${es12h ? horas : dosDigitos(horas)}:${dosDigitos(f.M)}` +
    (/ss/.test(tokens) ? `:${dosDigitos(f.S)}` : "") +
    (es12h ? (f.H >= 12 ? " PM" : " AM") : "");
  if (!/[ymd]/.test(tokens)) return hora; // formatos de solo hora
  const fecha = /mmmm/.test(tokens)
    ? `${f.d} de ${MESES_ES[f.m - 1]} de ${f.y}`
    : `${dosDigitos(f.d)}/${dosDigitos(f.m)}/${f.y}`;
  return /h/.test(tokens) ? `${fecha} ${hora}` : fecha;
}

// Excel guarda las fechas como seriales (44929 = 3 de enero de 2023) y el
// formateador de xlsx solo conoce los meses en inglés. Reescribimos el texto
// de cada celda de fecha en español para que el CSV y el PDF salgan igual que
// en la hoja original.
function normalizarFechas(worksheet: WorkSheet, SSF: XlsxModule["SSF"]): void {
  for (const ref of Object.keys(worksheet)) {
    if (ref.startsWith("!")) continue;
    const cell = worksheet[ref];
    if (!cell) continue;
    const formato = typeof cell.z === "string" ? cell.z : "";
    if (cell.v instanceof Date) {
      const d: Date = cell.v;
      cell.w = textoFecha(
        {
          y: d.getFullYear(),
          m: d.getMonth() + 1,
          d: d.getDate(),
          H: d.getHours(),
          M: d.getMinutes(),
          S: d.getSeconds(),
        },
        formato || "dd/mm/yyyy",
      );
      continue;
    }
    if (cell.t !== "n" || typeof cell.v !== "number") continue;
    if (!formato || !SSF.is_date(formato)) continue;
    const f = SSF.parse_date_code(cell.v) as FechaExcel | null;
    if (f) cell.w = textoFecha(f, formato);
  }
}

// Excel da como rango de la hoja todo lo que alguna vez tuvo formato, no sólo
// lo que tiene datos: las hojas de viáticos (11.5) declaran A1:BN32, 66
// columnas, para 9 columnas y unas pocas filas con datos, y el PDF salía con
// decenas de columnas vacías apretando la tabla. Se recorta el rango a la
// última fila y la última columna con contenido.
function recortarAlContenido(
  worksheet: WorkSheet,
  utils: XlsxModule["utils"],
): void {
  const ref = worksheet["!ref"];
  if (!ref) return;
  let ultimaFila = -1;
  let ultimaColumna = -1;
  for (const celda of Object.keys(worksheet)) {
    if (celda.startsWith("!")) continue;
    const cell = worksheet[celda];
    if (!cell || String(cell.w ?? cell.v ?? "").trim() === "") continue;
    const { r, c } = utils.decode_cell(celda);
    ultimaFila = Math.max(ultimaFila, r);
    ultimaColumna = Math.max(ultimaColumna, c);
  }
  if (ultimaFila < 0) return; // hoja sin datos: nada que recortar
  const rango = utils.decode_range(ref);
  rango.e.r = Math.min(rango.e.r, ultimaFila);
  rango.e.c = Math.min(rango.e.c, ultimaColumna);
  worksheet["!ref"] = utils.encode_range(rango);
}

function isExcelFile(formato: string): boolean {
  const formatLower = formato?.toLowerCase() || "";
  return (
    formatLower.includes("xls") ||
    formatLower.includes("xlsx") ||
    formatLower.includes("excel")
  );
}

async function fetchFileViaProxy(url: string): Promise<ArrayBuffer> {
  const proxyUrl = `/api/proxy-file?url=${encodeURIComponent(url)}`;
  const response = await fetch(proxyUrl);
  if (!response.ok) throw new Error(`Error al descargar archivo: ${response.status}`);
  return response.arrayBuffer();
}

async function convertXlsxToCsv(url: string, filename: string): Promise<void> {
  const XLSX = await import("xlsx");
  const { saveAs } = await import("file-saver");
  const arrayBuffer = await fetchFileViaProxy(url);
  const workbook = XLSX.read(arrayBuffer, { type: "array", cellNF: true });
  const worksheet = workbook.Sheets[workbook.SheetNames[0]];
  normalizarFechas(worksheet, XLSX.SSF);
  recortarAlContenido(worksheet, XLSX.utils);
  const csv = XLSX.utils.sheet_to_csv(worksheet);
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  saveAs(blob, filename.replace(/\.xlsx?$/i, ".csv"));
}

async function convertXlsxToPdf(url: string, filename: string): Promise<void> {
  const XLSX = await import("xlsx");
  const { jsPDF } = await import("jspdf");
  const autoTable = (await import("jspdf-autotable")).default;
  const arrayBuffer = await fetchFileViaProxy(url);
  const workbook = XLSX.read(arrayBuffer, { type: "array", cellNF: true });
  const doc = new jsPDF({ orientation: "landscape" });
  workbook.SheetNames.forEach((sheetName, index) => {
    if (index > 0) doc.addPage();
    const worksheet = workbook.Sheets[sheetName];
    normalizarFechas(worksheet, XLSX.SSF);
    recortarAlContenido(worksheet, XLSX.utils);
    // raw: false usa el texto formateado de cada celda (fechas, montos, %)
    // en vez del valor crudo, que para las fechas es el serial de Excel.
    const jsonData = XLSX.utils.sheet_to_json(worksheet, {
      header: 1,
      raw: false,
      defval: "",
    }) as string[][];
    if (jsonData.length > 0) {
      // Sin título encima: el nombre de la hoja ("2025", "sep 2024") no
      // corresponde al documento. La tabla arranca en el margen superior.
      autoTable(doc, {
        head: [jsonData[0] || []],
        body: jsonData.slice(1),
        styles: { fontSize: 8, cellPadding: 2 },
        headStyles: { fillColor: [0, 22, 137], textColor: 255, fontStyle: "bold" },
        alternateRowStyles: { fillColor: [245, 247, 250] },
      });
    }
  });
  doc.save(filename.replace(/\.xlsx?$/i, ".pdf"));
}

function FileIcon({ formato }: { formato: string }) {
  const iconClass = "w-4 h-4";
  const f = formato?.toLowerCase() || "";
  if (f.includes("pdf")) return <FileText className={`${iconClass} text-red-500`} />;
  if (f.includes("xls") || f.includes("xlsx") || f.includes("excel"))
    return <FileText className={`${iconClass} text-green-600`} />;
  if (f.includes("doc") || f.includes("docx") || f.includes("word"))
    return <FileText className={`${iconClass} text-blue-600`} />;
  return <FileText className={iconClass} />;
}

interface Seguimiento {
  id: number;
  tipo: string;
  titulo: string;
  archivo: string;
  formato: string;
}

// Fila simple de descarga para documento principal o seguimiento
function ArchivoRow({
  titulo,
  archivo,
  formato,
  tipo,
  isExcel,
}: {
  titulo: string;
  archivo: string;
  formato: string;
  tipo?: string;
  isExcel?: boolean;
}) {
  const [isConverting, setIsConverting] = useState<"pdf" | "csv" | null>(null);

  const handleConvert = useCallback(
    async (type: "pdf" | "csv", e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (isConverting) return;
      setIsConverting(type);
      try {
        if (type === "csv") await convertXlsxToCsv(archivo, titulo);
        else await convertXlsxToPdf(archivo, titulo);
      } catch (error) {
        console.error(`Error al convertir a ${type}:`, error);
        alert(`Error al convertir el archivo a ${type.toUpperCase()}`);
      } finally {
        setIsConverting(null);
      }
    },
    [archivo, titulo, isConverting],
  );

  if (isExcel) {
    return (
      <div className="w-full rounded-lg bg-white border border-gray-100 overflow-hidden">
        <div className="flex items-center gap-3 p-3 min-w-0">
          <div className="shrink-0 w-8 h-8 rounded-lg bg-green-50 flex items-center justify-center">
            <FileSpreadsheet className="w-4 h-4 text-green-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-700 truncate">{titulo}</p>
            {tipo && <p className="text-xs text-amber-600 capitalize">{tipo}</p>}
          </div>
        </div>
        <div className="flex border-t border-gray-100 divide-x divide-gray-100">
          <a
            href={archivo}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 flex items-center justify-center gap-1.5 py-2 px-3 text-xs font-medium text-green-700 bg-green-50/50 hover:bg-green-100 transition-colors"
          >
            <FileSpreadsheet className="w-3.5 h-3.5" />XLSX
          </a>
          <button
            onClick={(e) => handleConvert("pdf", e)}
            disabled={isConverting !== null}
            className="flex-1 flex items-center justify-center gap-1.5 py-2 px-3 text-xs font-medium text-red-700 bg-red-50/50 hover:bg-red-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isConverting === "pdf" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5" />}
            {isConverting === "pdf" ? "Convirtiendo..." : "PDF"}
          </button>
          <button
            onClick={(e) => handleConvert("csv", e)}
            disabled={isConverting !== null}
            className="flex-1 flex items-center justify-center gap-1.5 py-2 px-3 text-xs font-medium text-blue-700 bg-blue-50/50 hover:bg-blue-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isConverting === "csv" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5" />}
            {isConverting === "csv" ? "Convirtiendo..." : "CSV"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <a
      href={archivo}
      target="_blank"
      rel="noopener noreferrer"
      className="group w-full flex items-center gap-3 p-3 rounded-lg bg-white border border-gray-200 hover:border-[#102542] hover:bg-gray-50 transition-colors overflow-hidden"
    >
      <div className="shrink-0 w-8 h-8 rounded-lg bg-gray-50 group-hover:bg-[#102542]/5 flex items-center justify-center transition-colors">
        <FileIcon formato={formato} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900 group-hover:text-[#102542] truncate transition-colors">
          {titulo}
        </p>
        {tipo ? (
          <p className="text-xs text-amber-700 capitalize">{tipo}</p>
        ) : (
          <p className="text-xs text-gray-400 uppercase tracking-wide">{formato || "Documento"}</p>
        )}
      </div>
      <Download className="w-4 h-4 text-gray-300 group-hover:text-[#102542] transition-colors shrink-0" />
    </a>
  );
}

interface DocumentoItemProps {
  documento: {
    id: number;
    titulo: string;
    archivo: string;
    formato: string;
    anio: number;
    seguimientos?: Seguimiento[];
  };
}

export default function DocumentoItem({ documento }: DocumentoItemProps) {
  const seguimientos = documento.seguimientos ?? [];
  const isExcel = isExcelFile(documento.formato);
  const totalItems = 1 + seguimientos.length;
  const [open, setOpen] = useState(false);

  // Sin seguimientos: mostrar el documento directamente
  if (seguimientos.length === 0) {
    return (
      <ArchivoRow
        titulo={documento.titulo}
        archivo={documento.archivo}
        formato={documento.formato}
        isExcel={isExcel}
      />
    );
  }

  // Con seguimientos: toggle manual para evitar problemas de layout del AccordionTrigger

  return (
    <div className="w-full border border-gray-200 rounded-lg overflow-hidden bg-white">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors text-left min-w-0"
      >
        <div className="shrink-0 w-8 h-8 rounded-lg bg-gray-50 flex items-center justify-center">
          <FileIcon formato={documento.formato} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-700 truncate">{documento.titulo}</p>
          <p className="text-xs text-gray-400 uppercase tracking-wide">{documento.formato || "Documento"}</p>
        </div>
        <span className="shrink-0 text-[10px] uppercase tracking-wider font-semibold text-[#102542] border border-[#102542]/30 bg-[#102542]/5 px-2 py-0.5 rounded-full">
          {totalItems} archivos
        </span>
        <ChevronDown
          className={`w-4 h-4 text-gray-400 shrink-0 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <div className="px-3 pb-3 grid gap-2 border-t border-gray-100 pt-3">
          <ArchivoRow
            titulo={documento.titulo}
            archivo={documento.archivo}
            formato={documento.formato}
            isExcel={isExcel}
          />
          {seguimientos.map((seg) => (
            <ArchivoRow
              key={seg.id}
              titulo={seg.titulo}
              archivo={seg.archivo}
              formato={seg.formato}
              tipo={seg.tipo}
              isExcel={isExcelFile(seg.formato)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
